import { nb, type MessageKey } from './catalogs';
import type { LocaleContextValue } from './LocaleProvider';

// ═══════════════════════════════════════════════════════════════════
// AN API ERROR CODE, SHOWN TO A PERSON (#310a).
//
// THE CODE CROSSES THE WIRE, NOT THE SENTENCE. The server answers
// `invalid_credentials`; the browser decides what that says and in which
// language. Three reasons, and the third is the one that bites later:
//
//   • the API has no idea which language a given browser is in, and giving it
//     one means a header nobody sets consistently and a catalog in two places;
//   • a code is stable, so a reworded message is a UI change rather than a
//     contract change;
//   • a translated sentence must never be PERSISTED as business data — an
//     audit row or a stored error that says «Feil e-post eller passord» is
//     wrong the moment anyone reads it in English, and cannot be corrected
//     without rewriting history.
//
// So nothing here ever goes back to the server.
// ═══════════════════════════════════════════════════════════════════

/** Codes this build knows how to say. Anything else becomes `errors.unknown`. */
const KNOWN = new Set<string>(
  Object.keys(nb)
    .filter((k) => k.startsWith('errors.'))
    .map((k) => k.slice('errors.'.length)),
);

/**
 * Turn an API error code into something a person can read.
 *
 * An UNKNOWN code becomes the generic message rather than being shown raw. A
 * user has no use for `rate_limited_per_identity`, and a screen that prints
 * whatever the server said is how internal vocabulary leaks into the product.
 * The raw code is still logged for whoever has to diagnose it.
 */
export function errorMessage(t: LocaleContextValue['t'], code: unknown): string {
  const raw = typeof code === 'string' ? code : '';
  if (raw && KNOWN.has(raw)) return t(`errors.${raw}` as MessageKey);
  if (raw) console.warn('[i18n] unmapped API error code:', raw);
  return t('errors.unknown');
}

/**
 * A refusal that carries a more specific `reason` beside its `error` (#1361,
 * #1366): `409 { error: 'unpriceable', reason: 'no_booked_price' }`. The reason
 * is said when this build has a sentence for it — `reason ?? error` — and the
 * code's own sentence otherwise, so a reason that is not a catalogued code (a
 * provider's, say) never turns a known refusal into «something went wrong».
 */
export function refusalMessage(
  t: LocaleContextValue['t'],
  error: unknown,
  reason?: unknown,
): string {
  const detail = typeof reason === 'string' ? reason : '';
  return errorMessage(t, detail && KNOWN.has(detail) ? detail : error);
}

/** Exposed so a test can prove the set is derived, not hand-maintained. */
export const knownErrorCodes = (): string[] => [...KNOWN].sort();
